// Single source of truth for "what we quoted vs what it actually billed."
//
// Mirrors the invoice math in `api/invoice.js` — keep the two in sync:
//   quoted = estimate.total_estimate (the quote we GAVE the client — it
//            already folds in travel/materials/heavy-item/removal/packing)
//            + Σ(additional_charges)
//   actual = quoted + (actual_hours − estimated_hours) × hourly_rate
//
// Quoted uses the estimate's `estimated_hours`; actual uses the recap's
// `actual_hours`. Everything except hours applies to both sides equally,
// so the only thing that moves between quoted and actual is the hours (which
// is exactly the over/under signal we want to surface on the Records page).
//
// Flat-priced jobs (no hourly rate — e.g. BOL / long-distance) have no hours
// variance: quoted == actual == the stored estimate total.

// `additional_charges` is a JSONB column (sql/014), so its runtime type
// depends on the delivery path: REST reads and Realtime echoes deliver a
// parsed ARRAY; legacy office writes stored JSON.stringify'd STRINGS
// (jsonb string scalars) which come back as strings. QA M6 OBS-1 root
// cause: consumers did a bare JSON.parse, which THROWS on the array form
// — charges silently rendered as [] after the realtime echo, and the
// +Charge modal then read "existing = []" and overwrote the whole list
// (adding a second charge deleted the first). Normalize both forms here;
// new writes store real arrays.
export function parseCharges(job) {
    const raw = job?.additional_charges;
    if (raw == null || raw === '') return [];
    if (Array.isArray(raw)) return raw;
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

// Returns a normalized billing snapshot for one job + its estimate.
//   isHourly      — true when there's an hourly rate (false = flat/BOL)
//   quotedHours   — estimated hours (falls back to scheduled duration)
//   actualHours   — recap hours, or null if not recapped
//   hasActual     — whether a recap with hours exists
//   quotedTotal   — $ we quoted
//   actualTotal   — $ it actually billed, or null if not recapped
//   displayTotal  — actualTotal when present, else quotedTotal
//   hoursDelta    — actual − quoted hours (null if no actual); <0 = under
//   dollarsDelta  — actual − quoted $    (null if no actual); <0 = under
export function jobBilling(job, estimate) {
    const rate = Number(estimate?.hourly_rate) || 0;
    const quotedHours = Number(estimate?.estimated_hours) || Number(job?.duration) || 0;

    const rawActual = job?.actual_hours;
    const hasActual = rawActual != null && rawActual !== '' && Number(rawActual) > 0;
    const actualHours = hasActual ? Number(rawActual) : null;

    const charges = parseCharges(job);
    const chargesTotal = charges.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);

    const isHourly = rate > 0;

    // Quoted = the estimate we actually gave the client (the stored
    // `total_estimate`, which already folds in travel/materials/heavy-item
    // fees), so Records and the estimate page never disagree. Plus any
    // line-item charges added after the fact. Fall back to labor math only
    // when there's no stored estimate total.
    const baseQuote = Number(estimate?.total_estimate) || (quotedHours * rate);
    const quotedTotal = baseQuote + chargesTotal;

    // Actual = that same quote adjusted purely by how the hours came in vs
    // estimate: (actualHours − quotedHours) × rate. Anchoring to the quote
    // keeps the dollar delta a clean hours-variance figure. Flat/BOL jobs
    // (no hourly rate) don't move on hours.
    const actualTotal = hasActual
        ? round2(quotedTotal + ((isHourly ? (actualHours - quotedHours) : 0) * rate))
        : null;

    const displayTotal = actualTotal != null ? actualTotal : quotedTotal;

    const hoursDelta = (isHourly && hasActual) ? round2(actualHours - quotedHours) : null;
    const dollarsDelta = (actualTotal != null) ? round2(actualTotal - quotedTotal) : null;

    return {
        isHourly,
        rate,
        quotedHours,
        actualHours,
        hasActual,
        quotedTotal,
        actualTotal,
        displayTotal,
        hoursDelta,
        dollarsDelta,
    };
}

// ─── Estimate-level grouping (QA M6 BUG-4) ──────────────────────────────
//
// Multi-phase estimates (PACK+MOVE, multi-day) put SEVERAL completed job
// rows under ONE estimate — but the client was quoted once. Anchoring
// each row to the full estimate double-counts quoted AND actual revenue
// (a $1,000 two-phase move showed $2,000 quoted on Records).
//
// This helper takes ALL of an estimate's legs and prices the ESTIMATE:
//   quotedHours — the estimate's hours, once (fallback: Σ leg durations)
//   actualHours — Σ recapped legs' hours
//   quotedTotal — total_estimate + Σ all legs' additional charges
//   actualTotal — quoted adjusted by total hours variance, but only when
//                 EVERY leg is recapped (a half-recapped group would
//                 read as a bogus "under")
//
// A single-leg group is arithmetically identical to jobBilling(), so
// callers can use this uniformly (solo jobs = group of one).
export function phaseSeq(j) {
    const n = Number(j?.phase_sequence);
    return Number.isFinite(n) ? n : 0;
}

export function estimateGroupBilling(legs, estimate) {
    const rate = Number(estimate?.hourly_rate) || 0;
    const isHourly = rate > 0;

    const quotedHours = Number(estimate?.estimated_hours)
        || legs.reduce((s, j) => s + (Number(j?.duration) || 0), 0);

    const chargesTotal = legs.reduce(
        (s, j) => s + parseCharges(j).reduce((t, c) => t + (parseFloat(c.amount) || 0), 0), 0);

    const baseQuote = Number(estimate?.total_estimate) || (quotedHours * rate);
    const quotedTotal = baseQuote + chargesTotal;

    const legHasActual = j => j?.actual_hours != null && j.actual_hours !== '' && Number(j.actual_hours) > 0;
    const actualLegs = legs.filter(legHasActual);
    const hasActual = actualLegs.length > 0;
    const allActual = hasActual && actualLegs.length === legs.length;
    const actualHours = hasActual
        ? round2(actualLegs.reduce((s, j) => s + Number(j.actual_hours), 0))
        : null;

    const actualTotal = allActual
        ? round2(quotedTotal + ((isHourly ? (actualHours - quotedHours) : 0) * rate))
        : null;
    const displayTotal = actualTotal != null ? actualTotal : quotedTotal;

    const hoursDelta = (isHourly && allActual) ? round2(actualHours - quotedHours) : null;
    const dollarsDelta = (actualTotal != null) ? round2(actualTotal - quotedTotal) : null;

    return {
        isHourly,
        rate,
        quotedHours,
        actualHours,
        hasActual,
        allActual,
        legCount: legs.length,
        quotedTotal,
        actualTotal,
        displayTotal,
        hoursDelta,
        dollarsDelta,
    };
}

function round2(n) {
    return Math.round((n + Number.EPSILON) * 100) / 100;
}
